import React from 'react'
import KibTag, { type KibTagThemes } from '@chewy/kib-tag-react'

import i18n from '~/locales/i18n'
import type { Prescription } from '~/types/entities/prescriptions'

import styles from './OrderTable.module.scss'

export interface OrderTableRow {
  label: string
  parentPrescription?: Prescription
  prescription?: Prescription
  relativeTime?: string | null
  value: string
}

export interface OrderTableProps {
  cardHeaders?: string[]
  getRowKey?: (rowIndex: number, row: OrderTableRow[]) => string
  headers: string[]
  renderRowActions?: (rowIndex: number, row: OrderTableRow[]) => React.ReactNode
  rows: OrderTableRow[][]
  statusTag?: string | null
  statusTagTheme?: KibTagThemes
  subHeader?: string | null
}

const emptyCell: Readonly<OrderTableRow> = {
  label: '',
  value: '',
  relativeTime: null,
} as const

const renderActionsZone = (children: React.ReactNode) => (
  <div data-click-exclude="true">{children}</div>
)

const generateRowKey = (componentId: string, row: OrderTableRow[]): string => {
  const prescriptionId = row[0]?.prescription?.id
  const fillNumber =
    row.find((cell) => cell.label.includes('Fill number'))?.value || ''
  return `${componentId}-${prescriptionId || fillNumber}`
}

export const OrderTable = ({
  cardHeaders,
  headers,
  rows,
  statusTag,
  statusTagTheme = 'default',
  renderRowActions,
  getRowKey,
  subHeader,
}: OrderTableProps) => {
  const componentId = React.useId()

  if (!rows?.length || !headers?.length) return null

  return (
    <div className={styles.cardsContainer}>
      {rows.map((row, rowIndex) => {
        const rowKey = getRowKey
          ? getRowKey(rowIndex, row)
          : generateRowKey(componentId, row)

        const cardHeaderText =
          cardHeaders?.[rowIndex] ?? subHeader ?? i18n.t('Common:ORDER')

        return (
          <div className={styles.orderCard} key={rowKey}>
            <div className={styles.cardHeader}>
              <span className={styles.cardHeaderText}>{cardHeaderText}</span>
              {statusTag && (
                <div className={styles.cardHeaderActions}>
                  <KibTag className={styles.statusTag} theme={statusTagTheme}>
                    {statusTag}
                  </KibTag>
                </div>
              )}
            </div>
            <div className={styles.cardContent}>
              {headers.map((header, cellIndex) => {
                const cell = row[cellIndex] || emptyCell
                if (!cell.label && !cell.value) return null

                const fieldKey = cell.label || header

                return (
                  <div
                    className={styles.cardField}
                    key={`${rowKey}-field-${fieldKey}`}
                  >
                    <span className={styles.cardFieldLabel}>{cell.label}</span>
                    <div className={styles.cardFieldValue}>
                      <span className={styles.cardFieldText}>{cell.value}</span>
                      {cell.relativeTime && (
                        <span className={styles.cardFieldDateLabel}>
                          {cell.relativeTime}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
              {renderRowActions && (
                <div className={styles.cardActions}>
                  {renderActionsZone(renderRowActions(rowIndex, row))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
